import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function MovingAverageChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', CHART_HEIGHT)
      .style('overflow', 'visible');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const n = data.dates.length;
    const parsedDates = data.dates.map((d) => new Date(d));

    // Collect all values for Y domain
    const allValues = [
      ...data.raw,
      ...(data.shortMA || []),
      ...(data.longMA || []),
    ].filter((v) => v != null);

    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.15 || 1;

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Line generators
    const lineGen = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    // Raw data line (thin gray dotted)
    g.append('path')
      .datum(data.raw)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '3,3');

    // Short MA line (solid blue)
    if (data.shortMA) {
      g.append('path')
        .datum(data.shortMA)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2);
    }

    // Long MA line (solid orange)
    if (data.longMA) {
      g.append('path')
        .datum(data.longMA)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', '#f59e0b')
        .attr('stroke-width', 2);
    }

    // Crossover markers
    if (data.crossovers && data.crossovers.length > 0) {
      const triangleSize = 40;
      const triangleUp = d3.symbol().type(d3.symbolTriangle).size(triangleSize);
      const triangleDown = d3.symbol().type(d3.symbolTriangle).size(triangleSize);

      data.crossovers.forEach((co) => {
        const idx = co.dateIndex;
        if (idx < 0 || idx >= n) return;

        // Use shortMA value at crossover point for marker placement
        const yVal = data.shortMA && data.shortMA[idx] != null
          ? data.shortMA[idx]
          : data.raw[idx];
        if (yVal == null) return;

        const cx = x(parsedDates[idx]);
        const cy = y(yVal);

        if (co.type === 'bullish') {
          // Green up-triangle
          g.append('path')
            .attr('d', triangleUp)
            .attr('transform', `translate(${cx}, ${cy - 10})`)
            .attr('fill', '#16a34a')
            .attr('stroke', '#fff')
            .attr('stroke-width', 0.5);
        } else {
          // Red down-triangle (rotated 180 degrees)
          g.append('path')
            .attr('d', triangleDown)
            .attr('transform', `translate(${cx}, ${cy + 10}) rotate(180)`)
            .attr('fill', '#dc2626')
            .attr('stroke', '#fff')
            .attr('stroke-width', 0.5);
        }
      });
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay for mouse tracking
    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const bisect = d3.bisector((d) => d).left;
        const dateAtMouse = x.invert(mx);
        const timestamps = parsedDates.map((d) => d.getTime());
        let idx = bisect(timestamps, dateAtMouse.getTime());
        // Snap to nearest point
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = x(parsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: data.dates[idx],
          raw: data.raw[idx],
          shortMA: data.shortMA ? data.shortMA[idx] : null,
          longMA: data.longMA ? data.longMA[idx] : null,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Moving Average Crossover</div>
          <div style={styles.subtitle}>Short-term (3-period) vs long-term (6-period) moving averages</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No moving average data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Moving Average Crossover</div>
        <div style={styles.subtitle}>Short-term (3-period) vs long-term (6-period) moving averages</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="3,3" />
          </svg>
          <span>Raw Price</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" />
          </svg>
          <span>Short MA (3)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#f59e0b" strokeWidth="2" />
          </svg>
          <span>Long MA (6)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="14" height="14" viewBox="0 0 14 14">
            <polygon points="7,2 3,10 11,10" fill="#16a34a" />
          </svg>
          <span>Bullish Crossover</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="14" height="14" viewBox="0 0 14 14">
            <polygon points="7,12 3,4 11,4" fill="#dc2626" />
          </svg>
          <span>Bearish Crossover</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#9ca3af' }}>Raw</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.raw != null ? tooltip.raw.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>Short MA</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>
              ${tooltip.shortMA != null ? tooltip.shortMA.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#f59e0b' }}>Long MA</span>
            <span style={{ fontWeight: 600, color: '#f59e0b' }}>
              ${tooltip.longMA != null ? tooltip.longMA.toFixed(2) : '--'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
